import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    IconButton,
    Dialog,
    DialogContent,
    useTheme
} from '@mui/material';
import ReactApexChart from 'react-apexcharts';
import CloseIcon from '@mui/icons-material/Close';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ToggleButton from './toggleDayNight';
export default function FullScreenGraphsDialog({ open, onClose, graphs }) {
    const [mainGraphIndex, setMainGraphIndex] = useState(0);
    const theme = useTheme();
    const [darkMode, setDarkMode] = useState(false);

    // Enter fullscreen when dialog opens
    useEffect(() => {
        if (open) {
            const element = document.documentElement;
            if (element.requestFullscreen) {
                element.requestFullscreen();
            } else if (element.webkitRequestFullscreen) {
                element.webkitRequestFullscreen();
            } else if (element.msRequestFullscreen) {
                element.msRequestFullscreen();
            }
        }
    }, [open]);

    const customColors = {
        "darkTheme": {
            "Eboiler": "#29D19C",
            "W95 Buffer": "#6A5D99",
            "W55 Buffer": "#F5A623",
            "W7 Buffer": "#4FB6C0",
            "PageBg": "#343e59",
            "ChartBg": "#2b2d3e",
            "TextColor": "#ffffff",
            "GridColor": "#777777",
            "ChartBorderColor": "#777777",
            "ChartGradientColor": "#777777",
            "mode": "dark",
        },
        "lightTheme": {
            "Eboiler": "#1F9562 ",
            "W95 Buffer": "#483F5E ",
            "W55 Buffer": "#FEB019 ",
            "W7 Buffer": "#3F7C80 ",
            "PageBg": "#e0e0e0",
            "ChartBg": "#ffffff",
            "TextColor": "#000000",
            "GridColor": "#777777",
            "ChartBorderColor": "#777777",
            "ChartGradientColor": "#777777",
            "mode": "light",
        }
    }
    const colors = darkMode ? customColors.darkTheme : customColors.lightTheme;

    useEffect(() => {
        if (darkMode) {
            document.body.classList.add('dark-mode');
        } else {
            document.body.classList.remove('dark-mode');
        }
    }, [darkMode]);

    // Handle close and fullscreen exit together
    const handleClose = () => {
        if (document.fullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }
        onClose();
    };

    const handleGraphClick = (index) => {
        setMainGraphIndex(index);
    };
    const getChartOptions = (options, isSmallGraph = false,graphs) => {
        // First, determine which chart type it is based on the title
        const chartTitle = graphs[0]?.name || '';
        let chartColor;
        // Assign specific colors based on chart type
        switch (chartTitle.toLowerCase()) {
            case 'eboiler':
                chartColor = '#29D19C';
                break;
            case 'w95 buffer':
                chartColor = '#6A5D99';
                break;
            case 'w55 buffer':
                chartColor = '#F5A623';
                break;
            case 'w7 buffer':
                chartColor = '#4FB6C0';
                break;
            default:
                chartColor = '#00E396'; // fallback color
        }

        return {
            ...options,
            title: {
                ...options.title,
                text: chartTitle,
                style: {
                    fontSize: isSmallGraph ? '14px' : '20px',
                    fontWeight: '400',
                    padding: '10px',
                    color: colors.TextColor  // Make title white
                }
            },
            theme: {
                mode: colors.mode,
            },
           
            colors: [chartColor], // Use the specific color for each chart
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'dark',
                    type: 'vertical',
                    shadeIntensity: 0.2,
                    gradientToColors: [chartColor], // Use the same color for gradient
                    inverseColors: false,
                    opacityFrom: 0.7,
                    opacityTo: 0.4
                }
            },
            grid: {
                show: true,
                borderColor: '#777777',
                strokeDashArray: 3,
                xaxis: { lines: { show: true } },
                yaxis: { lines: { show: true } },
                padding: {
                    top: isSmallGraph ? 0 : 20,
                    right: isSmallGraph ? 5 : 20,
                    bottom: isSmallGraph ? 0 : 20,
                    left: isSmallGraph ? 5 : 20
                },
                
            },
            xaxis: {
                ...options.xaxis,
                labels: {
                    style: {
                        colors: colors.TextColor
                    }
                }
            },
            yaxis: {
                ...options.yaxis,
                labels: {
                    style: {
                        colors: colors.TextColor
                    }
                }
            },
            chart: {
                toolbar: {
                    show: !isSmallGraph, // Hide toolbar for small graphs
                    background: colors.ChartBg,
                    color: colors.TextColor
                },
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 800,
                    animateGradually: {
                        enabled: true,
                        delay: 150
                    },
                    dynamicAnimation: {
                        enabled: true,
                        speed: 350
                    }
                },
                background: colors.ChartBg
            }
            // ... rest of the options
        };
    };
   

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={false}
            fullScreen
            PaperProps={{
                sx: {
                    //background: "linear-gradient(135deg, #343e59 0%, #343e59 100%)",
                    //bgcolor: "#343e59",  //#2b2d3e  // Change this to your desired background color
                    bgcolor: colors.PageBg,
                    '&:fullscreen': {
                        padding: 0,
                        margin: 0,
                        width: '100vw',
                        height: '100vh'
                    }
                }
            }}
        >
            <DialogContent sx={{ p: 3 }}>
                {/* add night day button */}
                <ToggleButton onToggle={setDarkMode} isDarkMode={darkMode} />
                {graphs && graphs.length > 0 ? (
                    <Box sx={{ height: '90%', display: 'flex', flexDirection: 'column', gap: 3, marginTop: "5%" }}>
                        {/* Main Graph */}
                        <Box sx={{
                            height: '60%',
                            borderRadius: 2,
                            boxShadow: 1,
                            //border: '1px solid #aaa',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                                transform: 'scale(1.01)',
                            },
                        }}>
                            <ReactApexChart
                                options={getChartOptions(graphs[mainGraphIndex].options,false,graphs[mainGraphIndex].series)}
                                series={graphs[mainGraphIndex].series}
                                type="area"
                                height="100%"
                                width="100%"
                                
                            />
                        </Box>

                        {/* Bottom Graphs */}
                        <Box sx={{ height: '35%', marginTop: "3%" }}>  {/* Increased height from 30% to 35% */}
                            <Grid container spacing={3}>
                                {graphs.map((graph, index) => (
                                    index !== mainGraphIndex && (
                                        <Grid key={index} item xs={4}>
                                            <Box
                                                onClick={() => handleGraphClick(index)}
                                                sx={{
                                                    height: '100%',
                                                    borderRadius: 2,
                                                    boxShadow: 1,
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        transform: 'scale(1.02)',
                                                        boxShadow: 3,
                                                        bgcolor: 'rgba(0, 0, 0, 0.1)'
                                                    },
                                                    '&:active': {
                                                        transform: 'scale(0.98)'
                                                    }
                                                }}
                                            >
                                                <ReactApexChart
                                                    options={getChartOptions(graph.options, true,graph.series)} // Pass true for small graphs
                                                    series={graph.series}
                                                    type="area"
                                                    height="100%"
                                                    width="100%"
                                                />
                                            </Box>
                                        </Grid>
                                    )
                                ))}
                            </Grid>
                        </Box>
                    </Box>
                ) : null}
            </DialogContent>
        </Dialog>
    );

}